<template>
  <div class="mm-location-autocomplete" :class="wrapperClass" ref="rootEl">
    <div class="relative">
      <input
        ref="inputEl"
        type="text"
        v-model="query"
        :placeholder="placeholder"
        :class="inputClass"
        :disabled="disabled"
        :required="required"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="showDropdown"
        aria-haspopup="listbox"
        :aria-activedescendant="activeDescendant"
        @input="onInput"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keydown.escape="onEscape"
        @focus="onFocus"
        @blur="onBlur"
      />
      <!-- Loading indicator -->
      <span v-if="loading" class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg class="animate-spin h-4 w-4 text-neutral-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </span>
    </div>

    <!-- Dropdown -->
    <ul
      v-if="showDropdown"
      class="absolute z-[9999] mt-1 w-full max-h-60 overflow-auto bg-white border border-neutral-200 shadow-lg"
      role="listbox"
      ref="listEl"
    >
      <li
        v-for="(item, idx) in results"
        :key="idx"
        :id="'mm-loc-opt-' + idx"
        role="option"
        :aria-selected="idx === highlightIndex"
        class="px-3 py-2 text-sm cursor-pointer transition-colors"
        :class="idx === highlightIndex ? 'bg-orange-100 text-black' : 'hover:bg-neutral-50'"
        @mousedown.prevent="selectItem(item)"
        @mouseenter="highlightIndex = idx"
      >
        <span class="font-medium">{{ item.place_name }}</span>
        <span v-if="item.full_name && item.full_name !== item.place_name" class="text-neutral-500 ml-1">{{ item.full_name.replace(item.place_name, '').replace(/^,\s*/, ', ') }}</span>
      </li>
      <li v-if="!results.length && !loading && query.length >= 2" class="px-3 py-2 text-sm text-neutral-500 italic">
        {{ noResultsText }}
      </li>
    </ul>

    <!-- Error message -->
    <p v-if="errorMsg" class="text-red-600 text-xs mt-1">{{ errorMsg }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
  modelValue: { type: Object, default: null },
  placeholder: { type: String, default: 'Zoek een plaats...' },
  inputClass: { type: String, default: 'w-full border border-transparent bg-white/95 px-4 py-3 text-sm shadow-inner placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-black/10' },
  wrapperClass: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  noResultsText: { type: String, default: 'Geen plaatsen gevonden' },
  initialText: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'select', 'clear'])

const rootEl = ref(null)
const inputEl = ref(null)
const listEl = ref(null)

const query = ref(props.initialText || (props.modelValue?.display_name || ''))
const results = ref([])
const loading = ref(false)
const errorMsg = ref('')
const highlightIndex = ref(-1)
const showDropdown = ref(false)
const selectedLocation = ref(props.modelValue || null)
let debounceTimer = null
let blurTimer = null

const activeDescendant = computed(() => {
  return highlightIndex.value >= 0 ? 'mm-loc-opt-' + highlightIndex.value : undefined
})

// Sync initial text from parent
watch(() => props.initialText, (val) => {
  if (val && !selectedLocation.value) {
    query.value = val
  }
})

watch(() => props.modelValue, (val) => {
  if (val && val.display_name) {
    query.value = val.display_name
    selectedLocation.value = val
  } else if (!val) {
    selectedLocation.value = null
  }
}, { deep: true })

function onInput() {
  // User is typing freely: clear the selected location
  if (selectedLocation.value) {
    selectedLocation.value = null
    emit('update:modelValue', null)
    emit('clear')
  }
  errorMsg.value = ''
  highlightIndex.value = -1

  if (debounceTimer) clearTimeout(debounceTimer)

  if (query.value.trim().length < 2) {
    results.value = []
    showDropdown.value = false
    return
  }

  debounceTimer = setTimeout(() => {
    fetchSuggestions(query.value.trim())
  }, 300)
}

async function fetchSuggestions(q) {
  if (q.length < 2) return
  loading.value = true
  errorMsg.value = ''
  showDropdown.value = true

  try {
    const ajaxUrl = window.materiaalAjax?.ajaxurl || '/wp-admin/admin-ajax.php'
    const url = new URL(ajaxUrl, window.location.origin)
    url.searchParams.set('action', 'mm_location_autocomplete')
    url.searchParams.set('q', q)
    url.searchParams.set('limit', '10')

    const res = await fetch(url.toString())
    if (!res.ok) throw new Error('Server error')

    const data = await res.json()
    if (data.ok && Array.isArray(data.results)) {
      results.value = data.results
    } else {
      results.value = []
    }
  } catch (e) {
    errorMsg.value = 'Kon locaties niet ophalen. Probeer het opnieuw.'
    results.value = []
  } finally {
    loading.value = false
  }
}

function selectItem(item) {
  query.value = item.display_name
  selectedLocation.value = item
  results.value = []
  showDropdown.value = false
  highlightIndex.value = -1
  errorMsg.value = ''
  emit('update:modelValue', item)
  emit('select', item)
}

function onArrowDown() {
  if (!showDropdown.value && query.value.length >= 2) {
    fetchSuggestions(query.value)
    return
  }
  if (highlightIndex.value < results.value.length - 1) {
    highlightIndex.value++
    scrollToHighlighted()
  }
}

function onArrowUp() {
  if (highlightIndex.value > 0) {
    highlightIndex.value--
    scrollToHighlighted()
  }
}

function onEnter() {
  if (highlightIndex.value >= 0 && highlightIndex.value < results.value.length) {
    selectItem(results.value[highlightIndex.value])
  }
}

function onEscape() {
  showDropdown.value = false
  highlightIndex.value = -1
}

function onFocus() {
  if (blurTimer) { clearTimeout(blurTimer); blurTimer = null }
  if (results.value.length && !selectedLocation.value) {
    showDropdown.value = true
  }
}

function onBlur() {
  // Delay to allow click on dropdown item
  blurTimer = setTimeout(() => {
    showDropdown.value = false
    // If user typed but didn't select, show validation hint
    if (query.value.length > 0 && !selectedLocation.value && props.required) {
      errorMsg.value = 'Selecteer een plaats uit de lijst'
    }
  }, 200)
}

function scrollToHighlighted() {
  nextTick(() => {
    if (!listEl.value) return
    const el = listEl.value.querySelector(`#mm-loc-opt-${highlightIndex.value}`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  })
}

// Close dropdown on outside click
function onDocClick(e) {
  if (rootEl.value && !rootEl.value.contains(e.target)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true)
  if (debounceTimer) clearTimeout(debounceTimer)
  if (blurTimer) clearTimeout(blurTimer)
})

// Expose for parent components
defineExpose({
  getSelectedLocation: () => selectedLocation.value,
  isValid: () => !!selectedLocation.value,
  clear: () => {
    query.value = ''
    selectedLocation.value = null
    results.value = []
    errorMsg.value = ''
    emit('update:modelValue', null)
    emit('clear')
  },
})
</script>

<style scoped>
.mm-location-autocomplete {
  position: relative;
}
</style>
